import { View, Text, Platform } from 'react-native';
import Head from 'expo-router/head';
import { AlertTriangle } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';

interface SectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
  colors: ReturnType<typeof usePublicThemeColors>;
  delay: number;
}

function Section({ number, title, children, colors, delay }: SectionProps) {
  return (
    <Animated.View entering={FadeInDown.delay(delay).duration(500)} style={{ marginBottom: 24 }}>
      <View className="flex-row items-center gap-2 mb-2">
        <AlertTriangle size={16} color="#D97706" />
        <Text
          style={{ color: colors.text.primary }}
          className="text-base font-bold"
        >
          {number}. {title}
        </Text>
      </View>
      <View>{children}</View>
    </Animated.View>
  );
}

function Paragraph({ children, colors }: { children: React.ReactNode; colors: ReturnType<typeof usePublicThemeColors> }) {
  return (
    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-2">
      {children}
    </Text>
  );
}

export default function DisclaimerScreen() {
  useTrackScreen('Disclaimer');
  const colors = usePublicThemeColors();

  return (
    <PublicLayout
      title="Financial Disclaimer"
      titleIcon={<AlertTriangle size={22} color="#D97706" />}
    >
      {Platform.OS === 'web' && (
        <Head>
          <title>Financial Disclaimer — Inverio</title>
          <meta name="description" content="Important disclaimers about Inverio's AI-generated scores and valuation estimates. Not financial advice." />
          <link rel="canonical" href="https://inverio.ai/disclaimer" />
          <meta property="og:title" content="Financial Disclaimer — Inverio" />
          <meta property="og:description" content="Important disclaimers about Inverio's AI-generated scores and valuation estimates. Not financial advice." />
          <meta property="og:url" content="https://inverio.ai/disclaimer" />
        </Head>
      )}
      {/* Draft Banner */}
      <Animated.View
        entering={FadeInDown.delay(100).duration(500)}
        className="mb-6 p-4 rounded-2xl flex-row items-start gap-3"
        style={{
          backgroundColor: '#FEF3C7',
          borderWidth: 1,
          borderColor: '#F59E0B',
        }}
      >
        <AlertTriangle size={20} color="#D97706" style={{ marginTop: 2 }} />
        <Text style={{ color: '#92400E' }} className="text-sm font-medium flex-1 leading-relaxed">
          This document is a draft and should be reviewed by a qualified legal professional before publication.
        </Text>
      </Animated.View>

      {/* Last Updated */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
        <Text style={{ color: colors.text.tertiary }} className="text-sm">
          Last updated: February 20, 2026
        </Text>
      </Animated.View>

      {/* Prominent Warning Banner */}
      <Animated.View
        entering={FadeInDown.delay(200).duration(500)}
        className="mb-8 p-5 rounded-2xl items-center"
        style={{
          backgroundColor: '#FFFBEB',
          borderWidth: 2,
          borderColor: '#F59E0B',
        }}
      >
        <View
          className="w-16 h-16 rounded-full items-center justify-center mb-4"
          style={{ backgroundColor: '#FEF3C7' }}
        >
          <AlertTriangle size={32} color="#D97706" />
        </View>
        <Text style={{ color: '#92400E' }} className="text-lg font-bold text-center mb-2">
          Important Financial Disclaimer
        </Text>
        <Text style={{ color: '#A16207' }} className="text-sm text-center leading-relaxed">
          Inverio is an informational platform only. Nothing on this platform constitutes financial, investment, legal, or tax advice. All investment decisions are made at your own risk.
        </Text>
      </Animated.View>

      <Section number={1} title="Not Financial Advice" colors={colors} delay={250}>
        <Paragraph colors={colors}>
          Inverio is NOT a registered financial advisor, broker-dealer, investment adviser, or fiduciary under the laws of Switzerland or any other jurisdiction. We do not hold any financial services license, and the Service is not regulated by the Swiss Financial Market Supervisory Authority (FINMA) or any equivalent regulatory body.
        </Paragraph>
        <Paragraph colors={colors}>
          The Service is an informational technology platform that provides AI-powered tools for startup assessment and investor-startup matching. All content, scores, analyses, reports, and outputs generated by the Service are provided for informational and educational purposes only and do not constitute financial, investment, legal, tax, or other professional advice.
        </Paragraph>
      </Section>

      <Section number={2} title="AI-Generated Scores" colors={colors} delay={300}>
        <Paragraph colors={colors}>
          Investment readiness scores and other assessments generated by the Service are the output of algorithmic and artificial intelligence-based analysis. These scores are based on data provided by users and publicly available information, processed through proprietary and third-party AI models. They represent a computational assessment and NOT an investment recommendation, endorsement, or guarantee of any kind.
        </Paragraph>
        <Paragraph colors={colors}>
          AI-generated scores may be incomplete, inaccurate, or outdated. They do not account for all factors relevant to an investment decision, including but not limited to market conditions, regulatory changes, macroeconomic factors, or undisclosed risks. No guarantee of accuracy, reliability, or completeness is made regarding any score or output.
        </Paragraph>
      </Section>

      <Section number={3} title="No Investment Recommendations" colors={colors} delay={350}>
        <Paragraph colors={colors}>
          The matching of startups with investors through the Service is informational only and does not constitute a recommendation, solicitation, or offer to invest in any company, security, or financial instrument. The inclusion of any startup on the platform, or any match suggested by the Service, should not be interpreted as an endorsement or validation of that startup's business model, financial health, or investment potential.
        </Paragraph>
        <Paragraph colors={colors}>
          Users must conduct their own independent due diligence before making any investment decisions. This includes, but is not limited to, reviewing all relevant financial documents, consulting with qualified professionals, and independently verifying all information provided through the Service.
        </Paragraph>
      </Section>

      <Section number={4} title="Risk Warning" colors={colors} delay={400}>
        <View
          className="p-4 rounded-2xl mb-3"
          style={{
            backgroundColor: '#FEF2F2',
            borderWidth: 1,
            borderColor: '#FECACA',
          }}
        >
          <Text style={{ color: '#991B1B' }} className="text-sm font-semibold leading-relaxed">
            Investing in startups and early-stage companies carries significant risk, including the risk of total loss of your investment. The majority of startups fail, and investors may lose all capital invested.
          </Text>
        </View>
        <Paragraph colors={colors}>
          Past performance of any startup, score, or metric presented on the Service is not indicative of future results. Startup investments are illiquid and may require a long time horizon before any return, if any, is realized. You should only invest money that you can afford to lose entirely. Startup investing is not suitable for all individuals, and you should carefully consider your financial situation, investment objectives, and risk tolerance before making any investment.
        </Paragraph>
      </Section>

      <Section number={5} title="Professional Advice" colors={colors} delay={450}>
        <Paragraph colors={colors}>
          Before making any investment, financial, legal, or tax decisions, you should consult with qualified professionals who are licensed and authorized to provide such advice in your jurisdiction. This includes, but is not limited to, financial advisors, investment professionals, attorneys, and tax advisors who can evaluate your specific circumstances, objectives, and risk tolerance.
        </Paragraph>
        <Paragraph colors={colors}>
          The information provided through the Service should be used as a starting point for further research and professional consultation, not as a substitute for professional advice.
        </Paragraph>
      </Section>

      <Section number={6} title="Limitation" colors={colors} delay={500}>
        <Paragraph colors={colors}>
          Inverio and its officers, directors, employees, agents, and affiliates shall not be liable for any investment decisions made by users of the Service, including decisions to invest, not invest, or divest based on information, scores, or analyses provided through the Service. By using the Service, you acknowledge and accept that all investment decisions are made solely at your own risk and discretion.
        </Paragraph>
        <Paragraph colors={colors}>
          To the maximum extent permitted by Swiss law, Inverio disclaims any and all liability for losses, damages, or costs incurred as a result of reliance on any information, score, analysis, or output provided through the Service.
        </Paragraph>
      </Section>

      {/* Contact Footer */}
      <Animated.View entering={FadeInDown.delay(550).duration(500)}>
        <View
          className="p-4 rounded-2xl mt-2"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
            Questions?
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm">
            Contact us at support@inverio.ai
          </Text>
        </View>
      </Animated.View>
    </PublicLayout>
  );
}
